/**
 * Widget form defaults and data mapping utilities.
 *
 * Extracted from WidgetForm.js to reduce file size.
 * Contains DEFAULT_FORM_DATA, STEPS config, and helper functions
 * for mapping API responses to form state and preparing submit payloads.
 */

import {
  DEFAULT_WIDGET_CONFIG,
  DEFAULT_CREDIT_EXHAUSTION_BEHAVIOR,
  WIDGET_FIELD_MAPPINGS,
} from "../constants";
import { sanitizeUrl } from "../formatters";

// Translates API field names to frontend field names when loading a widget.
// Only used in this file; defined here because it acts on WIDGET_FIELD_MAPPINGS.
function applyApiToFrontendMappings(widgetData) {
  if (!widgetData) return widgetData;
  const mapped = { ...widgetData };
  Object.entries(WIDGET_FIELD_MAPPINGS.apiToFrontend).forEach(
    ([apiName, frontendName]) => {
      if (mapped[apiName] !== undefined) {
        mapped[frontendName] = mapped[apiName];
        delete mapped[apiName];
      }
    },
  );
  return mapped;
}

export const STEPS = [
  { id: 1, title: "Basic Info", description: "Widget name and description" },
  { id: 2, title: "Branding", description: "Colors, avatar, and messages" },
  {
    id: 3,
    title: "AI Behavior",
    description: "System prompt and model settings",
  },
  {
    id: 4,
    title: "Advanced",
    description: "Rate limits, domains, and persistence",
  },
];

export const DEFAULT_FORM_DATA = {
  name: "",
  display_name: "",
  status: "active",
  primary_color: DEFAULT_WIDGET_CONFIG.PRIMARY_COLOR_DEFAULT,
  secondary_color: DEFAULT_WIDGET_CONFIG.SECONDARY_COLOR_DEFAULT,
  avatar_url: "",
  welcome_message: "Hello! How can I help you today?",
  placeholder_text: "Type your message...",
  branding_text: DEFAULT_WIDGET_CONFIG.BRANDING_TEXT_DEFAULT,
  branding_url: "",
  show_branding: true,
  system_prompt:
    "You are a helpful AI assistant. Be concise, friendly, and informative.",
  model: "", // Set from backend config endpoint on mount
  temperature: DEFAULT_WIDGET_CONFIG.TEMPERATURE_DEFAULT,
  max_tokens: DEFAULT_WIDGET_CONFIG.MAX_TOKENS_DEFAULT,
  rate_limit_per_ip: DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_DEFAULT,
  allowed_domains: "",
  persist_conversations: true,
  conversation_ttl_hours: DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_DEFAULT,
  credit_exhaustion_behavior: { ...DEFAULT_CREDIT_EXHAUSTION_BEHAVIOR },
};

/**
 * Map an API widget response to form state.
 * Applies field name mappings and fills defaults for missing fields.
 */
export function mapWidgetToFormData(apiWidget) {
  // Apply field mappings to convert API field names to frontend field names
  // (See WIDGET_FIELD_MAPPINGS in constants.js for full documentation)
  const widget = applyApiToFrontendMappings(apiWidget);

  return {
    name: widget.name || "",
    display_name: widget.display_name || "",
    status: widget.status || "active",
    primary_color:
      widget.primary_color || DEFAULT_WIDGET_CONFIG.PRIMARY_COLOR_DEFAULT,
    secondary_color:
      widget.secondary_color || DEFAULT_WIDGET_CONFIG.SECONDARY_COLOR_DEFAULT,
    avatar_url: widget.avatar_url || "",
    welcome_message:
      widget.welcome_message || DEFAULT_FORM_DATA.welcome_message,
    placeholder_text:
      widget.placeholder_text || DEFAULT_FORM_DATA.placeholder_text,
    branding_text:
      widget.branding_text || DEFAULT_WIDGET_CONFIG.BRANDING_TEXT_DEFAULT,
    branding_url: widget.branding_url || "",
    show_branding: widget.show_branding !== false,
    system_prompt: widget.system_prompt || DEFAULT_FORM_DATA.system_prompt,
    model: widget.model || "",
    temperature:
      widget.temperature ?? DEFAULT_WIDGET_CONFIG.TEMPERATURE_DEFAULT,
    max_tokens: widget.max_tokens ?? DEFAULT_WIDGET_CONFIG.MAX_TOKENS_DEFAULT,
    rate_limit_per_ip:
      widget.rate_limit_per_ip ??
      DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_DEFAULT,
    allowed_domains: widget.allowed_domains?.join("\n") || "",
    persist_conversations: widget.persist_conversations !== false,
    conversation_ttl_hours:
      widget.conversation_ttl_hours ??
      DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_DEFAULT,
    credit_exhaustion_behavior: widget.credit_exhaustion_behavior || {
      ...DEFAULT_CREDIT_EXHAUSTION_BEHAVIOR,
    },
  };
}

/**
 * Prepare form data for API submission.
 * Splits domains, sanitizes URLs, and applies field name mappings.
 */
export function prepareSubmitPayload(formData) {
  const widgetData = {
    ...formData,
    allowed_domains: formData.allowed_domains
      ? formData.allowed_domains
          .split("\n")
          .map((d) => d.trim())
          .filter(Boolean)
      : [],
  };

  // Sanitize URL fields before submission (defense-in-depth; backend also sanitizes)
  for (const field of ["avatar_url", "branding_url"]) {
    if (widgetData[field]) {
      widgetData[field] = sanitizeUrl(widgetData[field]);
    }
  }

  // Apply field name mappings (e.g., avatar_url → bot_avatar_url)
  // IMPORTANT: Only renames keys — do NOT apply type-specific transforms here.
  // URL sanitization is handled above (explicitly per-field) and on the backend.
  // See WIDGET_FIELD_MAPPINGS in constants.js for full documentation.
  Object.entries(WIDGET_FIELD_MAPPINGS.frontendToApi).forEach(
    ([frontendName, apiName]) => {
      if (widgetData[frontendName] !== undefined) {
        widgetData[apiName] = widgetData[frontendName];
        delete widgetData[frontendName];
      }
    },
  );

  return widgetData;
}
